import React, { useState, createContext, useContext, useEffect } from 'react';
import getRefreshToken from './lib/refresh';
import getAuthToken from './lib/auth';
import jsonwebtoken from 'jsonwebtoken';
import request, { endpoint } from './lib/request';

const ApiContext = createContext();

const STALE = 0;
const FRESH = 1;
const NONE = -1;

export function useApi() {
  return useContext(ApiContext);
}

export default function AuthProvider({ children, apiBaseUrl, ...props }) {
  const isTokenFresh = () => {
    const AUTH_TOKEN = sessionStorage.getItem('AUTH_TOKEN');
    const REFRESH_TOKEN = localStorage.getItem('REFRESH_TOKEN');
    const CLAIMS = sessionStorage.getItem('CLAIMS');

    if (AUTH_TOKEN === null) {
      if (REFRESH_TOKEN === null) {
        return NONE;
      } else {
        return STALE;
      }
    }

    if (CLAIMS !== null && REFRESH_TOKEN !== null) {
      const claimsObj = JSON.parse(CLAIMS);
      const now = new Date().getTime() / 1000;
      if (now > claimsObj.exp) {
        return STALE;
      }
      return FRESH;
    }

    return NONE;
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userRoles, setRoles] = useState([]);
  const [isFresh, setFresh] = useState(isTokenFresh());
  const [hasInitialised, setHasInitialised] = useState(false);

  let baseUrl = apiBaseUrl;
  if (baseUrl === undefined) {
    baseUrl = 'http://localhost:8000';
  }

  useEffect(() => setFresh(isTokenFresh()), [isAuthenticated]);

  const logout = () => {
    sessionStorage.clear();
    localStorage.removeItem('REFRESH_TOKEN');
    setIsAuthenticated(false);
    setUserId(null);
    setRoles([]);
  };

  const initialise = () => {
    if (hasInitialised) {
      return;
    }

    const verifyToken = (token, pubKey) => {
      try {
        claims = jsonwebtoken.verify(token, pubKey, {
          algorithms: ['RS256'],
          issuer: 'ptu-hub-api',
        });

        setRoles(claims.roles);
        setIsAuthenticated(true);
        setUserId(claims.sub);

        sessionStorage.setItem('AUTH_TOKEN', token);
        sessionStorage.setItem('CLAIMS', JSON.stringify(claims));
        setIsAuthenticated(true);
      } catch (e) {
        logout();
      }
    };

    const PK_URL = `${baseUrl}/.well-known/jwpub.pem`;
    const PUB_KEY_STR = sessionStorage.getItem('PUBLIC_KEY');
    let claims;

    if (isFresh === NONE) {
      setHasInitialised(true);
      return;
    }

    if (isFresh === STALE) {
      getRefreshToken(baseUrl).then((result) => {
        if (result) {
          setIsAuthenticated(true);
          claims = sessionStorage.getItem('CLAIMS');
          claims = JSON.parse(claims);
          setRoles(claims.roles);
          setUserId(claims.sub);
          setHasInitialised(true);
          return true;
        } else {
          logout();
          setHasInitialised(true);
          return false;
        }
      });
    }

    if (isFresh === FRESH) {
      const AUTH_TOKEN = sessionStorage.getItem('AUTH_TOKEN');
      // Get public key
      if (PUB_KEY_STR == null) {
        fetch(PK_URL, {
          method: 'GET',
          cache: 'no-store',
        })
          .then((response) => response.text())
          .then((text) => verifyToken(AUTH_TOKEN, text))
          .catch(() => {
            setHasInitialised(true);
            return false;
          });
        sessionStorage.setItem('PUBLIC_KEY', PUB_KEY_STR);
      } else {
        verifyToken(AUTH_TOKEN, PUB_KEY_STR);
      }
    }

    setHasInitialised(true);
  };

  // ********** RUN INIT FUNCTION ********** //
  initialise();

  const ApiInterface = {
    isAuthenticated,
    userId,
    userRoles,
    isFresh,
    request: {
      get: async (resource = [], options = {}) => {
        const ep = endpoint(baseUrl, resource);
        return await request(baseUrl, ep, null, options, 'GET');
      },

      post: async (resource = [], body = {}, options = {}) => {
        const ep = endpoint(baseUrl, resource);
        return await request(baseUrl, ep, body, options, 'POST');
      },

      put: async (resource = [], body = {}, options = {}) => {
        const ep = endpoint(baseUrl, resource);
        return await request(baseUrl, ep, body, options, 'PUT');
      },

      patch: async (resource = [], body = {}, options = {}) => {
        const ep = endpoint(baseUrl, resource);
        return await request(baseUrl, ep, body, options, 'PATCH');
      },

      delete: async (resource = [], body = {}, options = {}) => {
        const ep = endpoint(baseUrl, resource);
        return await request(baseUrl, ep, body, options, 'DELETE');
      },
    },

    logout,

    authenticate: async (username, passwordRaw) => {
      const hasGotToken = await getAuthToken(baseUrl, username, passwordRaw);
      if (!hasGotToken) {
        ApiInterface.logout();
        throw new Error('Authentication failed');
      }

      const claims = sessionStorage.getItem('CLAIMS');
      if (claims == null) {
        throw new Error('Authentication failed');
      }

      const claimsObj = JSON.parse(claims);
      setRoles(claimsObj.roles);

      setIsAuthenticated(true);
      setUserId(claimsObj.sub);
    },
  };

  return (
    <ApiContext.Provider value={ApiInterface}>
      {hasInitialised && children}
    </ApiContext.Provider>
  );
}
